import * as React from 'react';
import {useStore} from 'zustand';
import {persist, devtools, createJSONStorage} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {createStore} from 'zustand/vanilla';
import {WithSlices, withSlices} from '@/features/core/store/helpers';
import {shouldEnableDevelopmentTools} from '@/features/development';
import {migrateSettings} from '@/features/settings/store/migrations/migrate-settings';
import {settingsSlice} from '@/features/settings/store/settings-slice';

type RootStore = WithSlices<typeof settingsSlice>;

const PERSISTED_STATE_NAME = 'cosctl';
const PERSISTED_STATE_VERSION = new Date('2025-03-20').getTime();

const appStateCreator = immer<RootStore>(withSlices(settingsSlice));

const rootStore = createStore<RootStore>()(
  devtools(
    persist(appStateCreator, {
      name: PERSISTED_STATE_NAME,
      version: PERSISTED_STATE_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: (state, version) => ({
        ...(state as Record<string, unknown>),
        settings: migrateSettings(state, version),
      }),
    }),
    {
      enabled: shouldEnableDevelopmentTools,
    },
  ),
);

const defaultSelector = <T>(state: T) => state;

function useRootStore(): RootStore;
function useRootStore<T>(selector: (state: RootStore) => T): T;
function useRootStore<T>(selector?: (state: RootStore) => T): RootStore | T {
  const select = React.useMemo(() => selector ?? defaultSelector, [selector]);
  const store = useStore(rootStore);
  return select(store);
}

export {useRootStore, rootStore};
export type {RootStore};
